// Copyright (c) 2016 Deere & Company

import './credit-memo-report.scss';
import React, {
    Component,
    PropTypes
} from 'react';
import CreditMemo from '@deere/cps-it.warranty-model-factory/lib/credit-memo';
import CreditMemoReportCriteria from './credit-memo-report-criteria';
import Criteria from '../state/criteria';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import NumberOfRecordsFound from '../../../shared/components/number-of-records-found/index';
import ReportGrid from '../../shared/components/report-grid';
import ReportHeader from '../../shared/components/report-header';
import ReportHiddenColumns from '../../shared/components/report-columns';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import User from '@deere/cps-it.warranty-model-factory/lib/user';
import WarrantyAgGridDatePicker from '../../shared/components/ag-grid-date-picker';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {exportToCSV} from '../../../reports/shared/services/export-to-csv';
import {fixCursorPosition} from '../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../shared/services/format-pin';
import {getAgGridLocaleText} from '../../../shared/utils/ag-grid-locale-text';
import {getCreditMemoColumnDefinitions} from '../report-services/column-definitions';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {getSelectedRows} from '../../shared/services/get-selected-rows-from-in-memory-rows';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleCreateCreditMemoReport() {

    this.actions.toggleReportIsScrollable();
    this.actions.createCreditMemoReport(this.report.length, this.gridApi);

}

function handleClaimNumberInputChange(event) {

    this.actions.updateCreditMemoCriteria('claimNumber', event.target.value);

}

function handleClaimTypeSelect(claimType, isSelected) {

    this.actions.selectCreditMemoClaimType(claimType, isSelected);

}

function handleCreditMemoIssueDateSelect(creditMemoIssueDate) {

    this.actions.selectCreditMemoIssueDate(creditMemoIssueDate);

}

function handleDealerSelect(dealer, isSelected) {

    this.actions.selectCreditMemoDealer(dealer, isSelected);

}

function handleExportToCsv() {

    const fileName = 'credit-memo';

    return exportToCSV(fileName, this.gridApi);

}

function handleFirstWorkOrderInputChange(event) {

    this.actions.updateCreditMemoCriteria('workOrderOne', event.target.value);

}

function handlePinCriteriaInput(event) {

    const pinValue = formatPin(event.target.value);

    this.actions.updateCreditMemoCriteria('pin', pinValue);
    fixCursorPosition(event, pinValue);

}

function handleInvoiceRequestInputChange(event) {

    this.actions.updateCreditMemoCriteria('invoiceRequest', event.target.value);

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    if (this.isSapUp) {

        this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    }

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function handleCreditMemoSelect(event) {

    const creditMemoValue = formatPin(event.target.value);

    this.actions.updateCreditMemoCriteria('creditMemo', creditMemoValue);
    fixCursorPosition(event, creditMemoValue);

}

function handleSelectAllClaimTypes() {

    this.actions.selectAllCreditMemoClaimTypes(this.criteria.claimTypes.allowed);

}

function handleSelectAllDealers() {

    this.actions.selectAllCreditMemoDealers(this.selectedAccounts);

}

function handleCriteriaReset() {

    this.actions.resetCreditMemoReport();
    this.actions.updateShowNoResultsFound(false);

}

function handlePrintClaimsButtonClick() {

    const claims = getSelectedRows(this.props.gridApi.getModel().rowsToDisplay);

    this.props.actions.readCreditMemoClaims(claims, this.context.user.getLocale());

}

function handlePrintButtonClick() {

    const memos = getSelectedRows(this.props.gridApi.getModel().rowsToDisplay);

    this.props.actions.readCreditMemos(memos, this.context.user.getLocale());

}

function handleRowSelection() {

    const isDisabled = this.gridApi.getSelectedRows().length < 1;

    this.actions.updateDisplayCreditMemoDisabled(isDisabled);

}

function onColumnRemoved(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handleSelectDeselectAll() {

    this.showSelectAll ? this.gridApi.selectAllFiltered() : this.gridApi.deselectAllFiltered();
    this.actions.updateShowSelectAll(!this.showSelectAll);

}

class CreditMemoReport extends Component {

    async componentWillMount() {

        this.props.actions.readClaimTypes();
        this.props.actions.selectAllDealersOnLoad(this.props.selectedAccounts);
        await this.props.actions.getReportLayouts('credit-memo');
        this.props.actions.updateGridLayouts(this.props.layouts, getCreditMemoColumnDefinitions(this.context.user.privacy, this.props.isSapUp));

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();
            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetCreditMemoReport();
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetReportVariant();

    }

    render() {

        const canExportToExcel = this.props.report.length > 0;
        const gridOptions = {
            dateComponentFramework: WarrantyAgGridDatePicker
        };

        return (
            <section className='credit-memo'>
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                <h2 className='credit-memo-title'>
                    <FormattedMessage id='CREDIT_MEMO'/>
                    {this.props.isSapUp &&
                        <FavoriteIcon
                            id='CREDIT_MEMO'
                            route={this.props.pathName}
                        />
                    }
                </h2>
                <CreditMemoReportCriteria
                    canExport={canExportToExcel}
                    canRunReport={this.props.isCriteriaValid}
                    criteria={this.props.criteria}
                    handleAllClaimTypesSelect={this.props::handleSelectAllClaimTypes}
                    handleAllDealersSelect={this.props::handleSelectAllDealers}
                    handleClaimNumberSelect={this.props::handleClaimNumberInputChange}
                    handleClaimTypeSelect={this.props::handleClaimTypeSelect}
                    handleCreditMemoIssueDateSelect={this.props::handleCreditMemoIssueDateSelect}
                    handleCreditMemoSelect={this.props::handleCreditMemoSelect}
                    handleCriteriaReset={this.props::handleCriteriaReset}
                    handleDealerSelect={this.props::handleDealerSelect}
                    handleFirstWorkOrderInput={this.props::handleFirstWorkOrderInputChange}
                    handleInvoiceRequestInput={this.props::handleInvoiceRequestInputChange}
                    handlePinInputChange={this.props::handlePinCriteriaInput}
                    isSapUp={this.props.isSapUp}
                    onCreateCreditMemoReport={this.props::handleCreateCreditMemoReport}
                    onExportToCsv={this.props::handleExportToCsv}
                    selectedAccounts={this.props.selectedAccounts}
                    selectedDealers={this.props.selectedDealers}
                />
                {this.props.report.length ?
                    <div>
                        <NumberOfRecordsFound count={this.props.report.length}/>
                        <section className='report'>
                            {this.props.isReportColumnsVisible && <ReportHiddenColumns/>}
                            <ReportHeader
                                columnApi={this.props.columnApi}
                                gridApi={this.props.gridApi}
                                reportName='credit-memo'
                            >
                                {this.props.isSapUp &&
                                    <div className='display-credit-memo-button'>
                                        <button onClick={this.props::handleSelectDeselectAll}>
                                            {this.props.showSelectAll ?
                                                <FormattedMessage id='SELECT_ALL'/> :
                                                <FormattedMessage id='DESELECT_ALL'/>
                                            }
                                        </button>
                                        <button
                                            disabled={this.props.isDisplayCreditMemoDisabled}
                                            onClick={this::handlePrintClaimsButtonClick}
                                        >
                                            <FormattedMessage id='DISPLAY_CLAIM'/>
                                        </button>
                                        <button
                                            disabled={this.props.isDisplayCreditMemoDisabled}
                                            onClick={this::handlePrintButtonClick}
                                        >
                                            <FormattedMessage id='DISPLAY_CREDIT_MEMO'/>
                                        </button>
                                    </div>
                                }
                            </ReportHeader>
                            <ReportGrid
                                columnDefs={getCreditMemoColumnDefinitions(this.context.user.privacy, this.props.isSapUp)}
                                gridOptions={gridOptions}
                                localeText={getAgGridLocaleText()}
                                onColumnVisible={this.props::onColumnRemoved}
                                onGridReady={this.props::handleGridApiDefinition}
                                onRowSelected={this.props::handleRowSelection}
                                rowData={this.props.report}
                                rowDeselection={true}
                                rowSelection='multiple'
                            />
                        </section>
                    </div> :
                    <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

CreditMemoReport.displayName = 'CreditMemoReport';
CreditMemoReport.contextTypes = {
    user: PropTypes.instanceOf(User).isRequired
};
CreditMemoReport.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    criteria: PropTypes.instanceOf(Criteria).isRequired,
    gridApi: PropTypes.object.isRequired,
    isCriteriaValid: PropTypes.bool.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isDisplayCreditMemoDisabled: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    isSapUp: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(CreditMemo)),
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedDealers: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    showNoResultsFound: PropTypes.bool.isRequired,
    showSelectAll: PropTypes.bool.isRequired
};

export default CreditMemoReport;



// WEBPACK FOOTER //
// ./src/reports/credit-memo/components/credit-memo-report.js